import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft, Bell, Calendar, FileText, BookOpen, 
  CheckCheck, Clock, User, Heart, LogOut
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const API = `${BACKEND_URL}/api`;

const NOTIFICATION_ICONS = {
  weekly_focus: <Calendar className="w-5 h-5 text-primary" />,
  progress_note: <FileText className="w-5 h-5 text-green-600" />,
  shared_resource: <BookOpen className="w-5 h-5 text-purple-600" />
};

const NOTIFICATION_COLORS = {
  weekly_focus: 'bg-primary/10 border-primary/20',
  progress_note: 'bg-green-50 border-green-200',
  shared_resource: 'bg-purple-50 border-purple-200'
};

const NOTIFICATION_LABELS = {
  weekly_focus: 'Weekly Focus',
  progress_note: 'Session Note',
  shared_resource: 'Resource'
};

export default function ParentNotifications({ onLogout }) {
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // Safely parse user from localStorage
  let user = null;
  try {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      user = JSON.parse(userStr);
    }
  } catch (e) {
    console.error('Failed to parse user from localStorage');
  }

  useEffect(() => {
    fetchNotifications();
  }, []);

  const fetchNotifications = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        setError('Not authenticated');
        setLoading(false);
        return;
      }
      const response = await axios.get(`${API}/notifications`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setNotifications(response.data);
      setError(null);
    } catch (err) {
      console.error('Failed to load notifications:', err);
      setError('Failed to load notifications');
      toast.error('Failed to load notifications');
    } finally {
      setLoading(false);
    }
  };

  const markAsRead = async (notificationId) => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/notifications/${notificationId}/read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => 
        prev.map(n => n.id === notificationId ? { ...n, read: true } : n)
      );
    } catch (error) {
      console.error('Failed to mark as read');
    }
  };

  const markAllAsRead = async () => {
    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${API}/notifications/mark-all-read`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setNotifications(prev => prev.map(n => ({ ...n, read: true })));
      toast.success('All notifications marked as read');
    } catch (error) {
      toast.error('Failed to mark all as read');
    }
  };

  const handleNotificationClick = (notification) => {
    markAsRead(notification.id);
    
    // Navigate based on notification type
    if (notification.notification_type === 'weekly_focus') {
      navigate(`/weekly-focus/${notification.child_id}`);
    } else if (notification.notification_type === 'progress_note') {
      navigate(`/parent/feedback/${notification.child_id}`);
    } else if (notification.notification_type === 'shared_resource') {
      navigate(`/children/${notification.child_id}`);
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now - date;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  // Group notifications by date
  const groupedNotifications = notifications.reduce((acc, notification) => {
    const date = new Date(notification.created_at);
    const today = new Date();
    const yesterday = new Date(today);
    yesterday.setDate(yesterday.getDate() - 1);
    
    let key;
    if (date.toDateString() === today.toDateString()) {
      key = 'Today';
    } else if (date.toDateString() === yesterday.toDateString()) {
      key = 'Yesterday';
    } else {
      key = date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    }
    
    if (!acc[key]) acc[key] = [];
    acc[key].push(notification);
    return acc;
  }, {});

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <nav className="bg-white border-b border-stone-100 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} className="rounded-full">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div className="flex items-center gap-2">
              <Heart className="w-6 h-6 text-primary fill-primary/20" />
              <span className="text-xl font-serif font-medium text-primary-900">PlayFirst</span>
            </div>
          </div>
          <Button variant="outline" onClick={onLogout} className="rounded-full text-red-600 border-red-200 hover:bg-red-50">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          {/* Page Header */}
          <div className="flex items-center justify-between mb-8">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-primary/10 rounded-2xl flex items-center justify-center">
                <Bell className="w-7 h-7 text-primary" />
              </div>
              <div>
                <h1 className="text-3xl font-serif text-primary-900">Notifications</h1>
                <p className="text-stone-500">
                  {unreadCount > 0 ? `${unreadCount} unread` : 'All caught up!'}
                </p>
              </div>
            </div>
            {unreadCount > 0 && (
              <Button 
                variant="outline" 
                onClick={markAllAsRead}
                className="rounded-full"
                data-testid="mark-all-read-btn"
              >
                <CheckCheck className="w-4 h-4 mr-2" />
                Mark all read
              </Button>
            )}
          </div>

          {/* Loading State */}
          {loading ? (
            <div className="text-center py-12">
              <p className="text-stone-600">Loading notifications...</p>
            </div>
          ) : error ? (
            /* Error State */
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-red-400" />
              </div>
              <h3 className="text-xl font-serif text-primary-900 mb-2">Couldn't load notifications</h3>
              <p className="text-stone-500 mb-4">{error}</p>
              <Button onClick={fetchNotifications} className="rounded-full">
                Try Again
              </Button>
            </Card>
          ) : notifications.length === 0 ? (
            /* Empty State */
            <Card className="p-12 text-center bg-white rounded-3xl border border-stone-100">
              <div className="w-16 h-16 bg-stone-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Bell className="w-8 h-8 text-stone-400" />
              </div>
              <h3 className="text-xl font-serif text-primary-900 mb-2">No notifications yet</h3>
              <p className="text-stone-500">
                When your therapist shares updates, they'll appear here
              </p>
            </Card>
          ) : (
            /* Notifications List */
            <div className="space-y-6">
              {Object.entries(groupedNotifications).map(([date, dateNotifications]) => (
                <div key={date}>
                  <h3 className="text-sm font-medium text-stone-500 mb-3 px-2">{date}</h3>
                  <div className="space-y-3">
                    {dateNotifications.map((notification, index) => (
                      <motion.div
                        key={notification.id}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        transition={{ duration: 0.3, delay: index * 0.05 }}
                      >
                        <Card 
                          className={`p-4 rounded-2xl border cursor-pointer transition-all hover:shadow-md ${
                            notification.read 
                              ? 'bg-white border-stone-100' 
                              : NOTIFICATION_COLORS[notification.notification_type] || 'bg-blue-50 border-blue-200'
                          }`}
                          onClick={() => handleNotificationClick(notification)}
                          data-testid={`notification-${notification.id}`}
                        >
                          <div className="flex items-start gap-4">
                            <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${
                              notification.read ? 'bg-stone-100' : 'bg-white'
                            }`}>
                              {NOTIFICATION_ICONS[notification.notification_type] || <Bell className="w-5 h-5" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                <Badge variant="secondary" className="text-xs">
                                  {NOTIFICATION_LABELS[notification.notification_type] || 'Update'}
                                </Badge>
                                {!notification.read && (
                                  <span className="w-2 h-2 bg-primary rounded-full"></span>
                                )}
                              </div>
                              <p className="font-medium text-primary-900 mb-1">
                                {notification.notification_type === 'weekly_focus' && 'New weekly focus available'}
                                {notification.notification_type === 'progress_note' && 'New session feedback shared'}
                                {notification.notification_type === 'shared_resource' && 'New resource shared'}
                              </p>
                              <p className="text-sm text-stone-600 line-clamp-2">
                                {notification.therapist_name} shared an update for {notification.child_name}
                                {notification.details?.focus_statement && `: "${notification.details.focus_statement.slice(0, 60)}..."`}
                                {notification.details?.title && `: "${notification.details.title}"`}
                              </p>
                              <div className="flex items-center gap-4 mt-2 text-xs text-stone-400">
                                <span className="flex items-center gap-1">
                                  <Clock className="w-3 h-3" />
                                  {formatDate(notification.created_at)}
                                </span>
                                <span className="flex items-center gap-1">
                                  <User className="w-3 h-3" />
                                  {notification.child_name}
                                </span>
                              </div>
                            </div>
                          </div>
                        </Card>
                      </motion.div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Quick Links */}
          <div className="mt-8 flex flex-wrap gap-4 justify-center">
            <Button 
              variant="outline" 
              onClick={() => navigate('/parent/feedback')}
              className="rounded-full"
            >
              <FileText className="w-4 h-4 mr-2" />
              View All Feedback
            </Button>
            <Button 
              variant="outline" 
              onClick={() => navigate('/')}
              className="rounded-full"
            >
              Back to Dashboard
            </Button>
          </div>
        </motion.div>
      </main>
    </div>
  );
}
